import * as React from 'react';
import { HeaderMultipleFilterTable, Icon } from '@/components/common';
import Icons from '@/const/icons';
import { OptionsMultiple } from '@/components/ui/comboboxMultiple';
import { Options } from '@/components/ui/combobox';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_CLIENT_DIRECTORY_CASES_FILTER } from '@/api';
import { clientDirectoryService } from '@/api/clientDirectory';
import { CaseFilterListData } from '@/interfaces';
import { useFilters } from '@/hooks';
import { GC_TIME, STALE_TIME } from '@/const/common';

interface CasesFilterProps {
	title?: string;
}

export const CasesFilter: React.FC<CasesFilterProps> = () => {
	const LIMIT_PER_PAGE = 50;

	const optionsDefined: Options[] = [
		{
			label: '0 Cases',
			value: '0',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
		{
			label: '1 Case',
			value: '1',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
		{
			label: '+2 Cases',
			value: '2',
			icon: (
				<Icon
					src={Icons.note}
					width={18}
					height={18}
					className="text-primary"
				/>
			),
		},
	];
	const { setParamSeveralFilters, hasFilter } = useFilters();
	const [data, setData] = React.useState<OptionsMultiple[]>([]);
	const observer = React.useRef<IntersectionObserver | null>(null);

	const fetchData = async ({
		pageParam,
	}: {
		pageParam: number;
	}): Promise<CaseFilterListData> => {
		const response = await clientDirectoryService.getCaseListFilter(
			pageParam,
			LIMIT_PER_PAGE
		);
		return response.data;
	};

	const {
		data: caseNameList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isError,
		// isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CLIENT_DIRECTORY_CASES_FILTER],
		queryFn: fetchData,
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
		refetchOnWindowFocus: false,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (caseNameList) {
			const auxOptions: Options[] = [];

			const listFlatMapped =
				caseNameList?.pages.flatMap((page) => page.items) || [];
			listFlatMapped.map((item) =>
				auxOptions.push({
					label: item.title,
					value: item.id,
					icon: (
						<Icon
							src={Icons.briefcase}
							width={18}
							height={18}
							className="text-primary"
						/>
					),
				})
			);

			const auxData: OptionsMultiple[] = [
				{
					title: 'Case Name',
					options: auxOptions,
				},
				{
					title: 'Number of Cases',
					options: optionsDefined,
				},
			];
			setData(auxData);
		}
	}, [caseNameList]);

	const handleSelectFilters = (_key: string, value?: string) => {
		// specials filters
		switch (value) {
			case '0':
			case '1':
			case '2':
				if (value === '0' || value === '1' || value === '2') {
					setParamSeveralFilters([
						{ label: 'caseId', value: '' },
						{ label: 'totalCase', value: value },
					]);
				}
				break;

			default:
				setParamSeveralFilters([
					{ label: 'totalCase', value: '' },
					{ label: 'caseId', value: value },
				]);
				break;
		}
	};

	const handleClearAll = () => {
		setParamSeveralFilters([
			{ label: 'caseId', value: '' },
			{ label: 'totalCase', value: '' },
		]);
	};

	return (
		<HeaderMultipleFilterTable
			title="Cases"
			searchTitle="Cases"
			filterOptions={data}
			containerRef={lastItemCallback}
			hasFilter={hasFilter('totalCase') || hasFilter('caseId')}
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			classNamePopoverContent="!max-h-fit !w-[342px]"
			classNameCommandList="!px-0 "
		/>
	);
};
